import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { Typography } from "antd";
import { ConsumerTokens, EnigmaTokens } from "@type/tokens";
import { TextAlign } from "./table.types";

export const rowSelectionCheckboxClass = css`
    &&&& {
        * {
            transition: visibility 0s;
        }
    }
`;

export const StyledTableWrapper = styled.div<{
    isLoading: boolean;
    isScrollable: boolean;
    token: ConsumerTokens;
    height?: number | "auto";
}>(({ isLoading, isScrollable, token, height }) => {
    const tableHeight = typeof height === "number" ? `${height}px` : height;

    return `
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 12px;
    border: 1px solid ${token.color.border.borderSecondary};
    justify-content: ${isLoading ? "center" : "start"};
    ${
        tableHeight
            ? `
            height: ${tableHeight};
            overflow-y: auto;
        `
            : `min-height:35rem;`
    }
    ${
        isScrollable &&
        `
            overflow-x: auto;

            &:hover::-webkit-scrollbar-thumb {
                visibility: visible;
            }
        
            &::-webkit-scrollbar {
                width: 0;
                height: 8px;
            }
        
            &::-webkit-scrollbar-track {
                box-shadow: transparent;
            }
        
            &::-webkit-scrollbar-thumb {
                background-color: ${token.color.border.borderSecondary};
                outline: none;
                border-radius: 12px;
                visibility: hidden;
            }
        `
    }
`;
});

export const tableEmptyContainer = css`
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    height: 100%;
    width: 100%;
    flex-grow: 1;
`;

export const paginator = css`
    align-self: center;
`;

export const StyledTableShadowContainer = styled.div<{ withShadow: boolean }>(
    ({ withShadow }) => `
    position: relative;
    overflow: hidden;
    ${
        withShadow &&
        `
            &::after {
                position: absolute;
                top: 50%;
                left: 100%;
                width: 50px;
                transform: translateY(-50%);
                height: calc(100% - 28px);
                box-shadow: 0px 0 10px rgba(0, 0, 0, 0.2);
                content: '';
            }
        `
    }
`
);

export const mainContainer = css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: transparent;
`;

export const StyledTableHead = styled.thead<{
    token: ConsumerTokens;
    stickyHeader?: boolean;
}>(
    ({ token, stickyHeader }) => `
    background-color: ${token.color.bg.fill.fillSubtler};
    ${
        stickyHeader &&
        `
        position:sticky;
        top:0;
        z-index: 2;
        `
    }
   
`
);

export const StyledHeaderRow = styled.tr<{ token: ConsumerTokens }>(
    ({ token }) => `
        border-bottom: 1px solid ${token.color.border.borderSecondary};
`
);

export const getHeaderIconClass = (
    token: EnigmaTokens,
    enabled: boolean
) => css`
    cursor: pointer;
    margin: 0 ${token.spacing.spacing50};
    height: 1.25em;
    flex-wrap: nowrap;
    flex-shrink: 0;
    visibility: hidden;
    opacity: ${enabled ? "1" : "0.5"};

    &.active {
        visibility: visible;
    }
`;

export const headerIconBaseClass = "header-icon";

export const StyledHeaderCell = styled.th<{
    textAlign: TextAlign;
    truncate?: boolean;
    token: EnigmaTokens;
}>(
    ({ textAlign, truncate = false, token }) =>
        `
        font-size: 12px;
        color: ${token.color.text.textSecondary};
        padding: ${token.spacing.spacing150} ${token.spacing.spacing300};
        text-align: ${textAlign};
    ${
        truncate &&
        `
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        `
    }

    &:focus .${headerIconBaseClass} {
        visibility: visible;
    }

    &:hover .${headerIconBaseClass} {
        visibility: visible;
    } 
`
);

export const headerContent = css`
    display: flex;
`;

export const headerLabel = css`
    flex-wrap: wrap;
`;

export const headerIcon = css`
    flex-shrink: 0;
`;

export const selectBox = css`
    width: 32px;
`;

export const StyledTable = styled.table<{
    isLoading?: boolean;
    isScrollable?: boolean;
}>(
    ({ isLoading, isScrollable }) => `
    visibility: ${isLoading ? "hidden" : "visible"};
    width: 100%;
    border-spacing: 0;
    border-collapse: collapse;

    ${!isScrollable && `table-layout: fixed;`}
   
`
);

export const StyledRow = styled.tr<{
    isPseudoLastRow: boolean;
    isInteractiveRow: boolean;
    token: ConsumerTokens;
    isRowSelected: boolean;
}>(
    ({ isInteractiveRow, isPseudoLastRow, token, isRowSelected }) => `

    background-color: ${
        isRowSelected && token.color.bg.fill.fillPrimarySubtler
    };
    &:not(:last-child) {
        border-bottom: 1px solid ${token.color.border.borderSecondary};
    }

    &:hover {
        background-color: ${
            isRowSelected
                ? token.color.bg.fill.fillPrimarySubtler
                : token.color.bg.fill.fillSubtler
        };
    }
    cursor: ${isInteractiveRow ? "pointer" : "default"};
    ${
        isPseudoLastRow &&
        `
            border-bottom: 1px solid ${token.color.border.borderSecondary};
        `
    }
`
);

export const StyledCell = styled.div<{
    textAlign: TextAlign;
    truncate?: boolean;
    rowHeight?: number;
}>(
    ({ textAlign, rowHeight }) => `
    box-sizing: border-box;
    padding: 0 24px;
    overflow: hidden;
    font-size: 14px;
    height: ${rowHeight ? `${rowHeight}px` : "auto"};
    min-height: ${rowHeight ? "auto" : "50px"};
    &:not(th) {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
    }
    justify-content: ${textAlign};
`
);

export const loaderWrapper = css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
`;

export const defaultLoader = css`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    height: 100%;
    background-color: rgb(255 255 255 / 0.3);
`;

export const StyledNoDataLabel = styled(Typography.Text)<{
    token: ConsumerTokens;
}>(
    ({ token }) => `
        color: ${token.color.core.primary}
`
);
